import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'
import { ErrorPage } from './components/errors/ErrorPage'

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD, // Only enable in production
  release: __RELEASE_VERSION__,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of transactions for performance monitoring
  // Session Replay
  replaysSessionSampleRate: 1.0, // Sample 100% of sessions for replay
  replaysOnErrorSampleRate: 1.0, // Always sample sessions with errors
  // Enable source maps for better error tracking
  beforeSend(event) {
    // Filter out known third-party library errors and expected user-facing errors
    const message = event.exception?.values?.[0]?.value || '';

    // Drop xterm internal errors
    if (message.includes("Cannot read properties of undefined (reading 'dimensions')")) {
      return null;
    }

    // Drop expected AI/Workers errors (these are handled gracefully in the UI)
    if (message.includes('Workers AI limit') ||
        message.includes('exceeded') && message.includes('token') ||
        message.includes('AiError')) {
      return null;
    }

    // Drop user-friendly error messages (not actual bugs)
    const userFriendlyErrors = [
      'Your Odoo instance has a large schema',
      'Unable to connect to your Odoo instance',
      'The query took too long to execute',
      'Unable to connect to the database',
      'Something went wrong. Please try again',
    ];
    if (userFriendlyErrors.some(err => message.includes(err))) {
      return null;
    }

    // Add release context to all events
    if (event.release) {
      event.tags = { ...event.tags, deployment: event.release }
    }
    return event
  }
})

function GlobalErrorFallback() {
  return (
    <ErrorPage
      title="Something went wrong"
      message="An unexpected error occurred. Please try again or return to your projects."
    />
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<GlobalErrorFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
